// React
import React from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

// Libs
import { Card, Table } from 'semantic-ui-react';

// Components
import CopyContentComponent from '../../../misc/CopyContent.component';

// Services
import { numberFormat } from '../../../../services/misc/numberFormat';

// Types
import { DeliveryData } from '../../../../types/Delivery';
import dateToString from '../../../../services/misc/dateToString';

type LeadDeliveryComponentProps = {
    data: DeliveryData;
    copy?: boolean;
};

/**
 * Box for showing delivery costs
 * @param props LeadDeliveryComponentProps
 * @returns React.ReactElement
 */
export default function LeadDeliveryComponent(props: LeadDeliveryComponentProps): React.ReactElement {
    const { t } = useTranslation();

    const matrix = [
        ['product', 'product'],
        props.data.priceFree // is a special for PICK_UP type. Currently as a simply solution. Refactor if it is necessary.
            ? ['price', 'priceFree']
            : [
                  'price',
                  'priceNet',
                  `(${t('lead.detail.box.delivery.priceNet')})`,
                  ' | ',
                  'priceGross',
                  `(${t('lead.detail.box.delivery.priceGross')})`,
              ],

        props.data.deliveryDate
            ? ['deliveryDate', 'deliveryDate', ...(props.data.deliveryType === 'PICKUP' ? [' | ', 'deliveryTime'] : [])]
            : ['deliveryDate', `${t('lead.detail.box.delivery.soonAsPossible')}`],
    ];

    function createValue(values: Array<string> = []): React.ReactElement {
        const output = values.map((key) => {
            let _v = props.data[key] ? props.data[key] : key;

            if (key === 'product') {
                return i18next.t(`lead.detail.box.delivery.types.${_v}`);
            } else if (key === 'priceFree' && _v) {
                return i18next.t('lead.detail.box.delivery.priceFree');
            } else if (key === 'deliveryDate') {
                return dateToString(_v, 'dateShort');
            } else if (key === 'deliveryTime') {
                return t('lead.detail.box.delivery.deliveryTimes.' + _v);
            } else {
                return (key.match('price') ? numberFormat(_v, true) : _v) + ' ';
            }
        });

        return props.copy ? <CopyContentComponent>{output.join(' ')}</CopyContentComponent> : <>{output}</>;
    }

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header>{t('lead.detail.box.delivery.headline')}</Card.Header>
                <Card.Description>
                    <Table celled striped columns={2}>
                        <Table.Body>
                            {matrix.map((row: Array<string>, ix) => (
                                <Table.Row key={ix}>
                                    <Table.Cell width={4}>{t(`lead.detail.box.delivery.${row[0]}`)}</Table.Cell>
                                    <Table.Cell singleLine>{createValue(row.splice(1))}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Card.Description>
            </Card.Content>
        </Card>
    );
}
